import React, { useState } from "react";
import { useNavigate } from 'react-router';
import { Box, List, ListItemButton, ListItemText, Collapse, Toolbar } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getStyles } from "./styles";
import { Roles, AuthenticationAdminResponse } from "@beable/roster-backend-client-v1";
import { ExpandMore, ExpandLess } from "@mui/icons-material";

interface MenuProps {
  onClick: () => void;
  profile: AuthenticationAdminResponse | undefined;
}

interface MenuItem {
  title: string;
  route?: string;
  nestedItems?: MenuItem[];
}

function HeaderMenu({ onClick, profile }: MenuProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const navigate = useNavigate();
  const [open, setOpen] = useState<{ [key: string]: boolean }>({});

  const handleParentClick = (item: string) => {
    setOpen({ ...open, [item]: !open[item] });
  };

  const menuItemClick = (e: any, route: string) => {
    e.stopPropagation();
    navigate(route);
  };

  const adminMenuItems = [
    { title: 'Dashboard', route: 'dashboard' },
    {
      title: 'Roster', nestedItems: [
        { title: 'Import Data', route: 'upload' },
        { title: 'Batch Status', route: 'batch-status' },
        { title: 'Beable User Accounts', route: 'employee-batch' },
        { title: 'Remove Beable Account', route: 'remove-school' },
        { title: 'Summer Classes', route: 'summer-classes' },
        { title: 'Account Mapping', route: 'account-mapping' },
      ]
    },
    {
      title: 'Provision', nestedItems: [
        { title: 'Users', route: 'provision-users' },
        { title: 'Courses', route: 'provision-courses' },
        { title: 'Revoke', route: 'revoke-provision' },
        { title: 'Course Sets', route: 'course-sets' },
        { title: 'Closed Dates', route: 'closed-dates' },
        { title: 'Test Dates', route: 'test-dates' },
      ]
    },
    {
      title: 'Motivation', nestedItems: [
        { title: 'Manage Contests', route: 'manage-contests' },
        { title: 'Manage Prizes', route: 'manage-prizes' },
        { title: 'Manage Merchandise', route: 'manage-merchandise' },
        { title: 'Manage Question Helps', route: 'manage-question-helps' },
        { title: 'Manage Engagements', route: 'manage-engagements' },
      ]
    },
    {
      title: 'Customer Administration', nestedItems: [
        { title: 'User Support', route: 'support' },
        { title: 'Customer Setup', route: 'customer-setup' },
        { title: 'Customer Permissions', route: 'customer-permissions' },
        { title: 'Roster View', route: 'data-view' },
        { title: 'Course Schedule', route: 'course-schedule' },
        { title: 'Assessment Dates & Hours', route: 'assessment-dates-and-hours' },
        { title: 'Transform', route: 'transform' },
        { title: 'Personalize Evaluator Accounts', route: 'personalize-evaluator' }
      ]
    },
  ];

  const contentLoaderItem = { title: 'Content Loading', route: 'content-loading-redirect' };
  const standardsAlignmentItem = { title: 'Standards Alignment', route: 'standards' };

  const menuItemsByRole: Record<Roles, MenuItem[]> = {
    [Roles.districtadmin]: adminMenuItems,
    [Roles.itadmin]: adminMenuItems,
    [Roles.beableadmin]: [
      ...adminMenuItems, {
        title: 'Beable Administration', nestedItems: [
          { title: 'Admin Management', route: 'manage' },
          { title: 'Site Management', route: 'sites' },
          { title: 'SSO Management', route: 'sso' },
          { title: 'Staff Accounts', route: 'staff-accounts' },
          { title: 'Creatio Imports', route: 'creatio-imports' },
        ]
      },
      contentLoaderItem
    ],
    [Roles.standardscorrelator]: [standardsAlignmentItem],
    [Roles.contentloader]: [
      contentLoaderItem, standardsAlignmentItem
    ]
  }

  const menuItems = () => {
    if (!profile?.role) {
      return [];
    }

    return menuItemsByRole[profile.role];
  }

  return (
    <Box
      sx={styles.list}
      data-testid="hamburger-menu"
      role="presentation"
      onClick={onClick}
      onKeyDown={onClick}
    >
      <Toolbar />
      <List>
        {menuItems().map(item => {
          return (
            <React.Fragment key={`${item.title}-section`}>
              {item.nestedItems != null ? (
                <>
                  <ListItemButton key={item.title} onClick={() => handleParentClick(item.title)} >
                    <ListItemText primary={item.title} />
                    {open[item.title] ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse key={`${item.title}-collapse`} component="li" in={open[item.title]} timeout="auto" unmountOnExit>
                    <List disablePadding>
                      {item.nestedItems.map((nestedItem: any) => {
                        return (
                          <ListItemButton
                            key={nestedItem.title}
                            onClick={(e: any) => {
                              menuItemClick(e, '/' + nestedItem.route);
                            }}
                            sx={styles.menu_nestedItem}
                          >
                            <ListItemText data-testid={`${nestedItem.route}-link`} primary={nestedItem.title} key={nestedItem.key} />
                          </ListItemButton>
                        )
                      })}
                    </List>
                  </Collapse>
                </>
              ) : (
                <ListItemButton
                  key={item.title}
                  onClick={(e: any) => {
                    menuItemClick(e, '/' + item.route);
                  }}
                >
                  <ListItemText data-testid={`${item.route}-link`} primary={item.title} />
                </ListItemButton>
              )}
            </React.Fragment>)
        })}
      </List>
    </Box>
  );
}
export default HeaderMenu;
